export interface DadataSuggestion {
    value: string;
    data: {
        geo_lat: string | null;
        geo_lon: string | null;
        city: string | null;
        street: string | null;
        house: string | null;
        settlement: string | null;
    };
}

export interface DadataResponse {
    suggestions: DadataSuggestion[];
}

interface UseDadataReturn {
    addressSuggestions: Ref<DadataSuggestion[]>;
    showSuggestions: Ref<boolean>;
    activeSuggestionIndex: Ref<number>;
    isLoadingSuggestions: Ref<boolean>;

    fetchAddressSuggestions: (query: string, city: string, locations?: Record<string, string>[]) => Promise<DadataSuggestion[]>;
    hideSuggestions: () => void;
    handleAddressKeydown: (event: KeyboardEvent, onSelect: (suggestion: DadataSuggestion) => void) => void;
}

export const useDadataAddress = (): UseDadataReturn => {
    const config = useRuntimeConfig();
    const dadataApiKey = config.public.dadataApiKey;

    const addressSuggestions = ref<DadataSuggestion[]>([]);
    const showSuggestions = ref(false);
    const activeSuggestionIndex = ref(-1);
    const isLoadingSuggestions = ref(false);

    const fetchAddressSuggestions = async (
        query: string,
        city: string,
        locations?: Record<string, string>[],
    ): Promise<DadataSuggestion[]> => {
        if (!query || query.length < 3 || (!city && !locations)) {
            addressSuggestions.value = [];
            showSuggestions.value = false;
            return [];
        }

        isLoadingSuggestions.value = true;

        try {
            const { data, error } = await useFetch<DadataResponse>(
                'https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/address',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Authorization': `Token ${dadataApiKey}`
                    },
                    body: {
                        query,
                        count: 10,
                        from_bound: { value: 'settlement' },
                        to_bound: { value: 'house' },
                        locations: locations ?? [
                            {
                                country: 'Россия',
                                city
                            }
                        ],
                        restrict_value: true
                    }
                }
            );

            if (error.value) {
                console.error('Ошибка при получении подсказок адресов:', error.value);
                addressSuggestions.value = [];
                showSuggestions.value = false;
                return [];
            }

            const suggestions = data.value?.suggestions || [];
            addressSuggestions.value = suggestions;
            showSuggestions.value = suggestions.length > 0;
            activeSuggestionIndex.value = -1;

            return suggestions;
        } catch (error) {
            console.error('Ошибка при получении подсказок адресов:', error);
            addressSuggestions.value = [];
            showSuggestions.value = false;
            return [];
        } finally {
            isLoadingSuggestions.value = false;
        }
    };

    const hideSuggestions = (): void => {
        showSuggestions.value = false;
        addressSuggestions.value = [];
        activeSuggestionIndex.value = -1;
    };

    const handleAddressKeydown = (event: KeyboardEvent, onSelect: (suggestion: DadataSuggestion) => void): void => {
        if (!showSuggestions.value || !addressSuggestions.value.length) return;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                activeSuggestionIndex.value =
                    activeSuggestionIndex.value < addressSuggestions.value.length - 1
                        ? activeSuggestionIndex.value + 1
                        : 0;
                break;

            case 'ArrowUp':
                event.preventDefault();
                activeSuggestionIndex.value =
                    activeSuggestionIndex.value > 0
                        ? activeSuggestionIndex.value - 1
                        : addressSuggestions.value.length - 1;
                break;

            case 'Enter':
                event.preventDefault();
                if (activeSuggestionIndex.value >= 0 && activeSuggestionIndex.value < addressSuggestions.value.length) {
                    onSelect(addressSuggestions.value[activeSuggestionIndex.value]);
                }
                break;

            case 'Escape':
                event.preventDefault();
                hideSuggestions();
                break;

            case 'Tab':
                hideSuggestions();
                break;
        }
    };

    return {
        addressSuggestions,
        showSuggestions,
        activeSuggestionIndex,
        isLoadingSuggestions,
        fetchAddressSuggestions,
        hideSuggestions,
        handleAddressKeydown,
    };
};